import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check as CheckIcon,
  ChevronDown as ChevronDownIcon,
  Plus as PlusIcon,
  Users as UsersIcon,
} from 'lucide-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setCurrentOrgId } from '@/store/organizationSlice';
import { roleLabels } from '@/types/organization';

export function OrgSwitcher() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const organizations = useAppSelector((state) => state.organization.organizations);
  const currentOrgId = useAppSelector((state) => state.organization.currentOrgId);
  const user = useAppSelector((state) => state.auth.user);
  const isPendingWk = user?.accountStatus === 'pending_wk' || !user?.wkIdentity;

  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const currentOrg = organizations.find((o) => o.id === currentOrgId);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (orgId: string) => {
    dispatch(setCurrentOrgId(orgId));
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    setIsOpen(false);
    if (location.pathname === '/organizations') {
      setSearchParams({ create: 'true' }, { replace: true });
    } else {
      void navigate('/organizations?create=true');
    }
  };

  const handleViewAll = () => {
    setIsOpen(false);
    void navigate('/organizations');
  };

  if (organizations.length === 0) {
    return (
      <button
        className="org-switcher-btn"
        onClick={() => {
          if (isPendingWk) {
            void navigate('/organizations');
          } else if (location.pathname === '/organizations') {
            setSearchParams({ create: 'true' }, { replace: true });
          } else {
            void navigate('/organizations?create=true');
          }
        }}
        style={{ color: 'var(--color-primary)' }}
      >
        <PlusIcon />
        <span>{isPendingWk ? t('layout.organizations') : t('layout.createOrganization')}</span>
      </button>
    );
  }

  return (
    <div className="org-switcher" ref={dropdownRef}>
      <button
        className="org-switcher-btn"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('layout.switchOrganization')}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        <UsersIcon />
        <span className="org-switcher-name">{currentOrg?.name || 'Select Organization'}</span>
        <ChevronDownIcon className="org-switcher-icon" style={{ fontSize: 12 }} />
      </button>

      {isOpen && (
        <div className="org-switcher-dropdown">
          {organizations.map((org) => (
            <button
              key={org.id}
              className={`org-switcher-item ${org.id === currentOrgId ? 'active' : ''}`}
              onClick={() => {
                handleSelect(org.id);
              }}
            >
              <span className="org-switcher-item-check">
                {org.id === currentOrgId && <CheckIcon />}
              </span>
              <span className="org-switcher-item-name">{org.name}</span>
              <span className="org-switcher-item-role">{roleLabels[org.membership.role]}</span>
            </button>
          ))}

          <div className="org-switcher-divider" />

          <button className="org-switcher-action" onClick={handleViewAll}>
            <UsersIcon />
            <span>{t('layout.viewAllOrganizations')}</span>
          </button>

          {!isPendingWk && (
            <button className="org-switcher-action" onClick={handleCreateNew}>
              <PlusIcon />
              <span>{t('layout.createOrganization')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default OrgSwitcher;
